/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import { Box, Drawer, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { format } from 'date-fns'
import i18n from 'i18n'

import { useGET_notificationsQuery } from 'redux/api/notificationsApi'
import { RootState } from 'redux/store'
import { toggleNotifications } from 'redux/ui/common/themeUI'

import Oops from 'components/atoms/Oops'
import SkeletonLoader from 'components/atoms/loaders/SkeletonLoader'

import { ASIDE_WIDTH, BG_DARK_GRAY, ELEMENT_RADIUS, HEADER_HEIGHT } from 'utils/customizedTheme'
import { locale } from 'utils/helpers'

import { INotifications } from 'types'
import { useMedia } from 'utils/customHooks/useMedia'

const Notifications = () => {
  const theme = useTheme()
  const { isUnder900px } = useMedia()
  const dispatch = useDispatch()

  const openNotifications = useSelector((state: RootState) => state.theme.openNotifications)
  const openSidebar = useSelector((state: RootState) => state.theme.openSidebar)

  const [page, setPage] = useState(1)
  const [notifications, setNotifications] = useState([] as INotifications[])

  const {
    isLoading,
    data: notificationsData,
    error,
  } = useGET_notificationsQuery({
    search: '',
    page: page,
    ordering: 'id',
  }, {skip: !openNotifications})

  const hasMore = Boolean(notificationsData?.next)

  useEffect(() => {
    if (notificationsData?.results) {
      setNotifications([...notifications, ...notificationsData?.results])
    }
  }, [notificationsData?.results])

  return (
    <Drawer
    variant='temporary'
      sx={{
        zIndex: theme.zIndex.appBar - 2,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isUnder900px ? '80vw' : '25rem',
          height: '100%',
          paddingRight: openSidebar ? `${ASIDE_WIDTH}px` : '0',
          boxSizing: 'border-box',
          overflow: 'hidden',
          transition: 'all 0.2 ease'
        },
      }}
      anchor='right'
      open={openNotifications}
      onClose={() => dispatch(toggleNotifications())}
    >
      <Box sx={{ minHeight: HEADER_HEIGHT }} />
      <Box sx={{ overflow: 'auto', height: '100%' }}>
        {error ? (
          <Oops />
        ) : (
          <>
            {isLoading && <NotificationsLoader />}
            {notifications?.length > 0 &&
              notifications.map((notification, i) => (
                <NotificationsCard
                  key={i}
                  row={notification}
                  rowsLength={notifications.length}
                  index={i}
                  style={{}}
                  hasMoreRows={hasMore}
                  nextPageAction={() => setPage(page + 1)}
                />
              ))}
          </>
        )}
      </Box>
    </Drawer>
  )
}

export default Notifications

const NotificationsCard = ({ row, rowsLength, index, style, hasMoreRows, nextPageAction }) => {
  const handleNextPage = () => {
    if (hasMoreRows) {
      nextPageAction()
    }
  }

  const Row = (
    <Box
      sx={{
        padding: '0.4rem 0.8rem',
        ...style,
      }}
    >
      <Box
        sx={{
          background: BG_DARK_GRAY,
          padding: '1rem',
          borderRadius: ELEMENT_RADIUS,
          position: 'relative',
        }}
      >
        <NotificationsNoneIcon
          sx={{
            position: 'absolute',
            top: '0.7rem',
            right: '0.8rem',
          }}
        />

        <Typography
          sx={{ fontWeight: '700', fontSize: '0.9rem', '& p': {margin: '0.1rem 0'} }}
          dangerouslySetInnerHTML={{
            __html: row.text,
          }}
        />

        <Typography sx={{ opacity: '0.5', fontSize: '0.8rem' }}>
          {`${format(new Date(row.created_at), 'dd MMM yyyy', {
            locale: locale[i18n.language],
          })}, ${format(new Date(row.created_at), 'HH:mm:ss', {
            locale: locale[i18n.language],
          })}`}
        </Typography>
      </Box>
    </Box>
  )

  return (
    <>
      {rowsLength - 1 === index ? (
        <Waypoint key={index} onEnter={handleNextPage}>
          {Row}
        </Waypoint>
      ) : (
        <>{Row}</>
      )}
    </>
  )
}

const NotificationsLoader = () => {
  return (
    <>
      {Array.from({ length: 5 }).map((el, i) => (
        <SkeletonLoader key={i} width={'90%'} height={'95px'} sx={{ margin: '0 1rem' }} />
      ))}
    </>
  )
}
